import { ModalAction } from '../ModalAction'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { vertragForm } from '../../../templates'
import { updateFormField, type FormValues } from '@/core/components/form/formModel'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { getAt, setAt } from '@/core/lib/path'
import { useAutosave } from '@/core/hooks/useAutosave'
import { getDocBucketAddress } from '@/core/data/dokumente'
import {
  buildVertragRenderData,
  confirmVertragAndPublish,
  generateAndStoreVertrag,
  getVertragSignedAt,
  saveVertragCorrections,
} from '../../../flows/vertrag'
import { AskModal } from '@/core/components/ui/ask-modal'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { PdfRegion } from '@/core/components/modal/PdfRegion'
import { Callout, Card, ModalHead, Prompt, PromptBody, ScrollBar, toastError, toastSuccess } from 'admination-react'
import { organisationInformationSheet } from '@/core/api/organisation'
import { me } from '@/core/data/session'
import { InformationsblattSection } from '@/core/components/orgsettings/InformationsblattSection'

const FIELD_MAP: { section: string; field: string; token: string }[] = [
  { section: 'Teilnehmer', field: 'Name, Vorname', token: 'teilnehmer.nameVorname' },
  { section: 'Teilnehmer', field: 'Anschrift', token: 'teilnehmer.anschrift' },
  { section: 'Teilnehmer', field: 'Geburtsdatum', token: 'teilnehmer.geburtsdatum' },
  { section: 'Kostenträger', field: 'Kostenträger', token: 'kostentraeger' },
  { section: 'Kostenträger', field: 'Kundennummer', token: 'kundennummer' },
  { section: 'Kostenträger', field: 'Aktivierungsgutschein-Nr', token: 'avgsNummer' },
  { section: 'Maßnahme', field: 'Titel', token: 'massnahme.titel' },
  { section: 'Maßnahme', field: 'Nummer', token: 'massnahme.nummer' },
  { section: 'Maßnahme', field: 'Abschluss', token: 'massnahme.abschluss' },
  { section: 'Maßnahme', field: 'Datum Beginn', token: 'massnahme.beginn' },
  { section: 'Maßnahme', field: 'Datum Ende', token: 'massnahme.ende' },
  { section: 'Maßnahme', field: 'Coachingeinheiten', token: 'massnahme.einheiten' },
  { section: 'Maßnahme', field: 'Kosten', token: 'massnahme.kosten' },
]

function dataToForm(data: Record<string, unknown>): FormValues {
  const out: FormValues = {}
  for (const { section, field, token } of FIELD_MAP) {
    ;(out[section] ??= {})[field] = String(getAt(data, token) ?? '')
  }
  return out
}

function formToCorrections(values: FormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const { section, field, token } of FIELD_MAP) {
    const v = values[section]?.[field]
    setAt(out, token, typeof v === 'string' ? v : '')
  }
  return out
}

export function VertragDokument({ coachingId }: { coachingId: string | null }) {
  const { dispatch, reloadDashboard } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)

  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'form' | 'preview'>('form')
  const [path, setPath] = useState<string | null>(null)
  const [signedAt, setSignedAt] = useState<string | null>(null)
  const [traegerSigned, setTraegerSigned] = useState<boolean | null>(null)
  const [values, setValues] = useState<FormValues | null>(null)
  const [generating, setGenerating] = useState(false)
  const [failed, setFailed] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [missingSheetOwner, setMissingSheetOwner] = useState<boolean | null>(null)

  const dirty = useRef(false)
  const generatingRef = useRef(false)

  const setField = useCallback((path: string, v: unknown) => {
    if (generatingRef.current) return
    dirty.current = true
    setValues((prev) => updateFormField(prev, path, v))
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setFailed(false)
    setValues(null)
    setPath(null)
    setSignedAt(null)
    setTraegerSigned(null)
    dirty.current = false
    if (!coachingId) {
      setLoading(false)
      return
    }
    ;(async () => {
      const data = await buildVertragRenderData(coachingId)
      const signed = await getVertragSignedAt(coachingId)
      const existingPath = await getDocBucketAddress(coachingId, 'vertrag')
      if (cancelled) return
      setValues(dataToForm(data))
      if (existingPath) {
        setPath(existingPath)
        setSignedAt(signed)
        setView('preview')
        return
      }
      const result = await generateAndStoreVertrag(coachingId)
      if (cancelled) return
      setPath(result.path)
      setSignedAt(result.status === 'signed' ? result.signedAt : null)
      setTraegerSigned(result.status === 'generated' ? result.traegerSigned : null)
      setView('preview')
    })()
      .catch((e) => {
        console.error("Vertrag konnte nicht geladen werden:", e)
        if (!cancelled) {
          setFailed(true)
          toastError("Vertrag konnte nicht erstellt werden. Bitte erneut versuchen.")
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [coachingId])

  const save = useCallback(
    async (v: FormValues | null) => {
      if (!coachingId || !v) return
      await saveVertragCorrections(coachingId, formToCorrections(v))
    },
    [coachingId],
  )
  const { flush } = useAutosave(values, !loading && !!coachingId && !signedAt, save, 250, { form: 'vertrag', record: coachingId ?? undefined, label: "Vertrag", recover: setValues })

  async function toForm() {
    if (!coachingId || generating) return
    const fresh = await getVertragSignedAt(coachingId)
    if (fresh) {
      setSignedAt(fresh)
      return
    }
    setView('form')
  }

  async function generate() {
    if (!coachingId || generatingRef.current) return
    generatingRef.current = true
    setGenerating(true)
    setFailed(false)
    try {
      await (flush() ?? (dirty.current && values ? save(values) : undefined))
      dirty.current = false
      const result = await generateAndStoreVertrag(coachingId)
      setPath(result.path)
      setSignedAt(result.status === 'signed' ? result.signedAt : null)
      setTraegerSigned(result.status === 'generated' ? result.traegerSigned : null)
      setView('preview')
    } catch (e) {
      console.error("Vertrag konnte nicht erstellt werden:", e)
      setFailed(true)
      toastError("Vertrag konnte nicht erstellt werden. Bitte erneut versuchen.")
    } finally {
      generatingRef.current = false
      setGenerating(false)
    }
  }

  async function send() {
    if (!coachingId || sending) return
    setSending(true)
    try {
      if (!(await organisationInformationSheet()).vorhanden) {
        const identity = await me()
        setMissingSheetOwner(identity.isOwner)
        setConfirmOpen(false)
        setSending(false)
        return
      }
      const link = await confirmVertragAndPublish(coachingId)
      toastSuccess(`Vertrag an ${link.sent_to} gesendet.`)
      reloadDashboard()
      dispatch({ type: 'CLOSE_FOR', coachingId })
    } catch (e) {
      console.error("Vertrag bestätigen fehlgeschlagen:", e)
      toastError(
        friendlyErrorMessage(e, "Vertrag konnte nicht gespeichert werden. Bitte versuchen Sie es erneut."),
      )
      setSending(false)
      setConfirmOpen(false)
    }
  }

  const showForm = !loading && !generating && view === 'form' && !signedAt && (!failed || values !== null)

  return (
    <>
      <ModalHeader size={showForm ? 'form' : 'document'}>
        <div className="pill-cluster">
          {view === 'form' && !signedAt ? (
            <ModalAction
              icon="i-doc"
              primaryAction
              title="Dokument erstellen"
              loading={generating}
              onClick={() => void generate()}
              disabled={loading || generating || values === null}
            />
          ) : (
            !signedAt && (
              <>
                <ModalAction
                  icon="i-edit"
                  title="Korrigieren"
                  onClick={() => void toForm()}
                  disabled={loading || generating}
                />
                <ModalAction
                  icon="i-send"
                  primaryAction
                  title="Senden"
                  loading={sending}
                  onClick={() => setConfirmOpen(true)}
                  disabled={loading || generating || sending || !path}
                />
              </>
            )
          )}
        </div>
      </ModalHeader>
      {missingSheetOwner !== null && <Prompt
        aria-label="Informationsblatt fehlt"
        onEscape={() => setMissingSheetOwner(null)}
        topbar={<ModalHead title="Informationsblatt" titleIcon="i-doc" onClose={() => setMissingSheetOwner(null)} closeLabel="Schließen" />}
      ><PromptBody>
        {missingSheetOwner ? <InformationsblattSection onSaved={() => { setMissingSheetOwner(null); setConfirmOpen(true) }} /> : <p className="section-note">Bitte bitten Sie die Organisationsverwaltung, das Informationsblatt in den Organisationseinstellungen hochzuladen. Danach können Sie den Vertragslink versenden.</p>}
      </PromptBody></Prompt>}
      <AskModal
        open={confirmOpen}
        icon="i-signature"
        iconStroke
        variant="is-info"
        question="Vertrag zum Teilnehmer senden?"
        description="Der Teilnehmer erhält den Vertrag per E-Mail zur digitalen Unterschrift."
        confirmLabel="Ja"
        cancelLabel="Nein"
        busy={sending}
        onConfirm={() => void send()}
        onCancel={() => setConfirmOpen(false)}
      />
      {loading ? (
        <main className="pdf-region">
          <Card className="is-overlay" isLoading loadingLabel="Vertrag wird geladen …" />
        </main>
      ) : showForm ? (
        <>
          <main className="form" data-scroll-host ref={scrollerRef}>
            {values && <FormRenderer definition={vertragForm} values={values} onChange={setField} readOnly={generating} />}
          </main>
          <ScrollBar scrollerRef={scrollerRef} />
        </>
      ) : failed ? (
        <main className="pdf-region">
          <div className="pdf-placeholder">
            Vertrag konnte nicht erstellt werden. Bitte den Schritt erneut öffnen.</div>
        </main>
      ) : (
        <>
          <PdfRegion
            notice={traegerSigned === false && <Callout tone="warning" role="alert">
              Ohne Unterschrift des Trägers - noch kein fertiges Dokument. Hinterlegen Sie Ihre Unterschrift in den Einstellungen und erstellen Sie den Vertrag erneut.</Callout>}
            path={path}
            waiting={generating}
            loadingLabel={generating ? "Vertrag wird erstellt …" : "Vertrag wird geladen …"}
            placeholder={signedAt ? "Vertrag wird geladen…" : "Vertrag wird erstellt…"}
          />
        </>
      )}
    </>
  )
}
